import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "../lib/api";
import { useConfig } from "../lib/useConfig";
import { useLang } from "../lib/i18n";
import { KpiCard, PageHeader, StatusBadge } from "../lib/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ListTodo, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";

const ALL = "__all__";

export default function Recommendations() {
  const qc = useQueryClient();
  const { data: cfg } = useConfig();
  const { t } = useLang();
  const [filters, setFilters] = useState({});
  const [edit, setEdit] = useState(null);

  const { data } = useQuery({
    queryKey: ["recs", filters],
    queryFn: async () => (await api.get("/recommendations", { params: filters })).data,
  });
  const stats = data?.stats || {};

  const save = async () => {
    await api.patch(`/recommendations/${edit.id}`, {
      status: edit.status, owner: edit.owner, owner_org: edit.owner_org,
      due_date: edit.due_date, priority: edit.priority, follow_up: edit.follow_up,
    });
    toast.success(t("Recommandation mise à jour."));
    qc.invalidateQueries({ queryKey: ["recs"] });
    setEdit(null);
  };

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v === ALL ? undefined : v }));

  return (
    <div data-testid="recommendations-page">
      <PageHeader title={t("Suivi des Recommandations & Plan d'Action")} subtitle={t("Recommandations issues des rapports quotidiens de terrain")} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="rec-open" label={t("Recomm. ouvertes")} value={stats.open ?? "-"} icon={ListTodo} />
        <KpiCard testid="rec-high" label={t("Prioritaires")} value={stats.high_priority ?? "-"} icon={AlertTriangle} tone="red" />
        <KpiCard testid="rec-overdue" label={t("En retard")} value={stats.overdue ?? "-"} icon={Clock} tone="amber" />
        <KpiCard testid="rec-resolved" label={t("Résolues (semaine)")} value={stats.resolved_this_week ?? "-"} icon={CheckCircle2} tone="teal" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-6 shadow-sm flex flex-wrap gap-3 items-end">
        <div><label className="text-xs text-slate-500 block mb-1">{t("Statut")}</label>
          <Select value={filters.status || ALL} onValueChange={(v) => setF("status", v)}>
            <SelectTrigger className="w-40" data-testid="rec-filter-status"><SelectValue placeholder={t("Tous")} /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>{t("Tous")}</SelectItem>{(cfg?.rec_statuses || []).map((s) => <SelectItem key={s} value={s}>{t(s)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><label className="text-xs text-slate-500 block mb-1">{t("Priorité")}</label>
          <Select value={filters.priority || ALL} onValueChange={(v) => setF("priority", v)}>
            <SelectTrigger className="w-40" data-testid="rec-filter-priority"><SelectValue placeholder={t("Toutes")} /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>{t("Toutes")}</SelectItem>{(cfg?.priorities || []).map((s) => <SelectItem key={s} value={s}>{t(s)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>{t("Recommandation")}</TableHead><TableHead>{t("Zone")}</TableHead><TableHead>{t("Identifié")}</TableHead>
              <TableHead>{t("Priorité")}</TableHead><TableHead>{t("Responsable")}</TableHead><TableHead>{t("Échéance")}</TableHead>
              <TableHead>{t("Statut")}</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data?.items?.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">{t("Aucune recommandation.")}</TableCell></TableRow> :
                data?.items?.map((r) => (
                  <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`rec-row-${r.id}`}>
                    <TableCell className="max-w-[280px] text-sm">{r.recommendation}</TableCell>
                    <TableCell className="text-sm">{r.health_zone}</TableCell>
                    <TableCell className="font-mono text-xs">{r.date_identified}</TableCell>
                    <TableCell><StatusBadge status={r.priority} /></TableCell>
                    <TableCell className="text-sm">{r.owner || "-"}</TableCell>
                    <TableCell className="font-mono text-xs">{r.due_date || "-"}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell><Button variant="ghost" size="sm" onClick={() => setEdit({ ...r })} data-testid={`rec-edit-${r.id}`}>{t("Gérer")}</Button></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-lg">
          {edit && (<>
            <DialogHeader><DialogTitle>{t("Gérer la recommandation")}</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">{edit.recommendation}</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">{t("Statut")}</label>
                <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                  <SelectTrigger data-testid="rec-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{(cfg?.rec_statuses || []).map((s) => <SelectItem key={s} value={s}>{t(s)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-xs text-slate-500">{t("Priorité")}</label>
                <Select value={edit.priority} onValueChange={(v) => setEdit({ ...edit, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{(cfg?.priorities || []).map((s) => <SelectItem key={s} value={s}>{t(s)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-xs text-slate-500">{t("Responsable")}</label><Input value={edit.owner || ""} onChange={(e) => setEdit({ ...edit, owner: e.target.value })} /></div>
              <div><label className="text-xs text-slate-500">{t("Organisation")}</label><Input value={edit.owner_org || ""} onChange={(e) => setEdit({ ...edit, owner_org: e.target.value })} /></div>
              <div><label className="text-xs text-slate-500">{t("Échéance")}</label><Input type="date" value={edit.due_date || ""} onChange={(e) => setEdit({ ...edit, due_date: e.target.value })} /></div>
            </div>
            <div><label className="text-xs text-slate-500">{t("Commentaire de suivi")}</label><Input value={edit.follow_up || ""} onChange={(e) => setEdit({ ...edit, follow_up: e.target.value })} /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setEdit(null)}>{t("Annuler")}</Button><Button className="bg-[#F05A28] hover:bg-[#D84A1B]" onClick={save} data-testid="rec-save">{t("Enregistrer")}</Button></div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
